import { useQuery } from "@apollo/client"
import cn from "classnames"
import { type TFunction } from "i18next"
import React, { useState, useCallback } from "react"
import { useTranslation, Trans } from "react-i18next"
import SvgInline from "react-svg-inline"
import Shell from "./shell"
import { type FragmentType, getFragment, graphql } from "@/api"
import { type Order, type OrderItem, ClientRole } from "@/api/graphql"
import AddBundleToCart from "@/common/components/add_bundle_to_cart"
import AddOrderItemToCart from "@/common/components/add_order_item_to_cart"
import ConfirmLink from "@/common/components/confirm_link"
import SignPreview, { useSignPreview, type SignPreviewOptions } from "@/common/components/sign_preview"
import env from "@/common/env"
import Util from "@/common/util"
import subarrow from "@/images/layout/subarrow.png"
import { TrashFill, BundleOutline } from "@/skylticons"

const PreviousSignFragment = graphql(`
  fragment PreviousSign_OrderItem on OrderItem {
    id
    v2
    signType
    previewClass
    doubleSided
    previewSvg
    previewSvgB
    signTypeKey
    widthDisplay
    heightDisplay
    hangType
    colors
    localPrice
    localTaxedPrice
    adoptUrl
    fontCss
    metaProduct
    customerEdit
    description
    bundleItem
    exampleFixedPrice
    discontinued

    bundle {
      id
      itemNumber

      image {
        id
        webp
        png
      }
    }
  }
`)

const historyDoc = graphql(`
  query CustomerHistory {
    currentCustomer {
      id
      email
      __typename

      previousSigns {
        ...PreviousSign_OrderItem
      }

      confirmedOrders {
        id
        confirmedAt
        numberOfSigns
        localTotal
        localPrice
        documentTitle
        documentPath
        creditOrder
        paid
        payedAt
        paymentPath
        cancelable
        authorizedPath

        creditOrders {
          id
          createdAt
          localTotal
          localPrice
          documentTitle
          documentPath
          __typename
        }

        __typename
      }

      cancelledOrders {
        id
        confirmedAt
        numberOfSigns
        localTotal
        localPrice
        authorizedPath
        __typename
      }

      unconfirmedOrders {
        id
        createdAt
        numberOfSigns
        localTotal
        localPrice
        deletable
        destroyPath
        confirmPath
        __typename
      }
    }
  }
`)

const timeFormat = new Intl.DateTimeFormat(env.locale, {
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
})

const shortdate = (dateTime?: string | null) => {
  if (!dateTime) {
    return null
  }

  return timeFormat.format(new Date(dateTime))
}

const adoptLink = (t: TFunction, item: Pick<OrderItem, "adoptUrl" | "exampleFixedPrice">) => {
  if (!item.adoptUrl) {
    console.error("sign adoptUrl missing")
    return
  }

  if (item.exampleFixedPrice) {
    if (confirm(t("fixed_price.dialog_text")) == true) {
      window.location.href = item.adoptUrl
    }
  } else {
    window.location.href = item.adoptUrl
  }
}

interface PreviousSignProps {
  openPreview: (options: SignPreviewOptions) => void
  sign: FragmentType<typeof PreviousSignFragment>
  admin: boolean
  business: boolean
}

const PreviousSign: React.FC<PreviousSignProps> = ({ openPreview, sign: signFragment, admin, business }) => {
  const sign = getFragment(PreviousSignFragment, signFragment)
  const { t } = useTranslation()
  const svg = sign.doubleSided ? [sign.previewSvg || "", sign.previewSvgB || ""] : sign.previewSvg || ""
  const switchBackground =
    (sign.signType === "vinyl_text" || sign.signType === "contour_cut") && sign.colors === "White"
  const bundleSvg = sign.bundleItem && sign.previewSvg

  return (
    <tr className={`previewitem-${sign.id}`}>
      <td className={cn({ "bg-white-10": switchBackground })}>
        <div className="signimage" data-id={sign.id}>
          {(sign.v2 || bundleSvg) && (
            <div
              id={`sign_${sign.id}`}
              className={cn("signv2 clickable", sign.signType, sign.previewClass)}
              data-type={sign.signType}
              onClick={() =>
                openPreview({
                  svg,
                  transparent: true,
                  withBackground: true,
                  singleView: true,
                  signWidth: (sign.widthDisplay && parseInt(sign.widthDisplay)) || 0,
                  signHeight: (sign.heightDisplay && parseInt(sign.heightDisplay)) || 0,
                  signType: sign.signTypeKey || "",
                  fixMethod: sign.hangType || "",
                  metaProduct: sign.metaProduct || "",
                })
              }
            >
              {sign.doubleSided ? (
                <>
                  <SvgInline
                    component="div"
                    svg={sign.previewSvg || ""}
                    className="sign_wrap side-a data-hj-suppress"
                  />
                  <SvgInline
                    component="div"
                    svg={sign.previewSvgB || ""}
                    className="sign_wrap side-b data-hj-suppress"
                  />
                </>
              ) : (
                <SvgInline component="div" svg={sign.previewSvg || ""} className="data-hj-suppress" />
              )}
            </div>
          )}
          {sign.bundleItem && !bundleSvg && (
            <div className="bundle-item">
              {sign.bundle?.image?.png ? <img src={sign.bundle.image.png} /> : <BundleOutline />}
            </div>
          )}
        </div>
      </td>
      <td className="hidden sm:table-cell">
        {sign.bundleItem ? sign.description : t(`sign_type.${sign.signTypeKey}`)}
      </td>
      <td className="hidden md:table-cell">
        {!sign.bundleItem && (
          <>
            {t("signslist.width")}: {sign.widthDisplay}mm
            <br />
            {t("signslist.height")}: {sign.heightDisplay}mm
          </>
        )}
      </td>
      <td className="hidden md:table-cell">
        {!sign.bundleItem && <>{Util.isBlank(sign.hangType) ? t("hang_type.none") : t(`hang_type.${sign.hangType}`)}</>}
      </td>
      <td className="hidden md:table-cell">{sign.colors}</td>
      <td className="hidden md:table-cell">{business ? sign.localPrice : sign.localTaxedPrice}</td>
      <td className="controls">
        <p className="mb-0">
          {sign.bundleItem && sign.bundle?.id && (
            <AddBundleToCart bundleId={sign.bundle?.id} className="button-emphasis button-medium mb-0" />
          )}
          {!sign.bundleItem && !sign.discontinued && (
            <>
              {sign.customerEdit || !sign.v2 || admin ? (
                <a
                  className={`button button-emphasis button-medium mb-0 adopt_sign_link-${sign.id}`}
                  onClick={() => adoptLink(t, sign)}
                >
                  <Trans i18nKey="signslist.reorder" />
                </a>
              ) : (
                <AddOrderItemToCart orderItemId={parseInt(sign.id)} classes="button-medium mb-0" />
              )}
            </>
          )}
        </p>
      </td>
      {!Util.isBlank(sign.fontCss) && <style>{sign.fontCss}</style>}
    </tr>
  )
}

const PreviousSignSkeleton: React.FC = () => {
  return (
    <tr className="previewitem-skeleton">
      <td>
        <div className="signimage">
          <div className="signv2">
            <svg className="skeleton" width="450" height="100"></svg>
          </div>
        </div>
      </td>
      <td className="hidden sm:table-cell">
        <span className="skeleton">Plastic sign</span>
      </td>
      <td className="hidden md:table-cell">
        <span className="skeleton">Width: 100mm</span>
        <br />
        <span className="skeleton">Height: 100mm</span>
      </td>
      <td className="hidden md:table-cell">
        <span className="skeleton">None</span>
      </td>
      <td className="hidden md:table-cell">
        <span className="skeleton">White</span>
      </td>
      <td className="hidden md:table-cell">
        <span className="skeleton">€28,48</span>
      </td>
      <td className="controls">
        <p className="skeleton">
          <a className="button button-emphasis button-medium">Reorder</a>
        </p>
      </td>
    </tr>
  )
}

interface ConfirmedOrderProps {
  order: Pick<
    Order,
    | "id"
    | "confirmedAt"
    | "numberOfSigns"
    | "localTotal"
    | "localPrice"
    | "documentTitle"
    | "documentPath"
    | "creditOrder"
    | "paid"
    | "payedAt"
    | "paymentPath"
    | "cancelable"
    | "authorizedPath"
  > & {
    creditOrders: Array<
      { __typename: "Order" } & Pick<
        Order,
        "id" | "createdAt" | "localTotal" | "localPrice" | "documentTitle" | "documentPath"
      >
    >
  }
  business: boolean
}

const ConfirmedOrder: React.FC<ConfirmedOrderProps> = ({ order, business }) => {
  const { t } = useTranslation()

  return (
    <>
      <tr id={`order-${order.id}`}>
        <td className="date">{shortdate(order.confirmedAt)}</td>
        <td className="ordernr">{order.id}</td>
        <td className="signcount hidden md:table-cell">{order.numberOfSigns}</td>
        <td className="prices hidden md:table-cell">{business ? order.localPrice : order.localTotal}</td>
        <td className="hidden md:table-cell">
          <a href={order.documentPath}>{order.documentTitle}</a>
        </td>
        <td className="payment hidden sm:table-cell">
          {order.creditOrder || order.paid ? (
            shortdate(order.payedAt)
          ) : (
            <a href={order.paymentPath}>{t("payment.confirm")}</a>
          )}
        </td>
        <td className="controls">
          {order.cancelable && (
            <ConfirmLink
              prompt={t("misc.remove")}
              href={`/order/cancel/${order.id}`}
              className="button button-error button-medium cancel-link mb-0 hidden sm:flex"
            >
              {t("misc.cancel")}
            </ConfirmLink>
          )}
          <a href={order.authorizedPath} className="button button-primary button-medium view-link mb-0">
            {t("misc.show")}
          </a>
        </td>
      </tr>
      {order.creditOrders.map((credit, i) => (
        <tr key={i} className="creditorder" id={`order-${credit.id}`}>
          <td className="date">
            <span className="subarrow">
              <img src={subarrow} />
            </span>{" "}
            {shortdate(credit.createdAt)}
          </td>
          <td></td>
          <td className="hidden md:table-cell"></td>
          <td className="prices hidden md:table-cell">{business ? credit.localPrice : credit.localTotal}</td>
          <td className="hidden md:table-cell">
            <a href={credit.documentPath}>{credit.documentTitle}</a>
          </td>
          <td className="hidden sm:table-cell"></td>
          <td className="controls">
            <a href={credit.documentPath} className="button button-primary button-medium md:hidden">
              {credit.documentTitle}
            </a>
          </td>
        </tr>
      ))}
    </>
  )
}

const ConfirmedOrderSkeleton: React.FC = () => {
  return (
    <>
      <tr>
        <td className="date">
          <span className="skeleton">08/19/2020</span>
        </td>
        <td className="ordernr">
          <span className="skeleton">96743895</span>
        </td>
        <td className="signcount hidden md:table-cell">
          <span className="skeleton">2</span>
        </td>
        <td className="prices hidden md:table-cell">
          <span className="skeleton">€21.90</span>
        </td>
        <td className="hidden md:table-cell">
          <a className="skeleton">Invoice</a>
        </td>
        <td className="payment hidden sm:table-cell">
          <a className="skeleton">Confirm</a>
        </td>
        <td className="controls">
          <a className="button button-primary button-medium view-link skeleton mb-0">Show</a>
        </td>
      </tr>
    </>
  )
}

interface CancelledOrderProps {
  order: Pick<Order, "id" | "confirmedAt" | "numberOfSigns" | "localTotal" | "localPrice" | "authorizedPath">
  business: boolean
}

const CancelledOrder: React.FC<CancelledOrderProps> = ({ order, business }) => {
  const { t } = useTranslation()

  return (
    <tr id={`order-${order.id}`}>
      <td className="date">{shortdate(order.confirmedAt)}</td>
      <td className="ordernr">{order.id}</td>
      <td className="signcount hidden md:table-cell">{order.numberOfSigns}</td>
      <td className="prices hidden md:table-cell">{business ? order.localPrice : order.localTotal}</td>
      <td className="hidden md:table-cell"></td>
      <td className="hidden sm:table-cell"></td>
      <td className="controls">
        <a href={order.authorizedPath} className="button button-primary button-medium mb-0">
          {t("misc.show")}
        </a>
      </td>
    </tr>
  )
}

const CancelledOrderSkeleton: React.FC = () => {
  return (
    <tr>
      <td className="date">
        <span className="skeleton">07/08/2020</span>
      </td>
      <td className="ordernr">
        <span className="skeleton">85004068</span>
      </td>
      <td className="signcount hidden md:table-cell">
        <span className="skeleton">2</span>
      </td>
      <td className="prices hidden md:table-cell">
        <span className="skeleton">$25.64</span>
      </td>
      <td className="hidden md:table-cell"></td>
      <td className="hidden sm:table-cell"></td>
      <td className="controls">
        <a className="button button-primary button-medium skeleton">Show</a>
      </td>
    </tr>
  )
}

interface UnconfirmedOrderProps {
  order: Pick<
    Order,
    "id" | "createdAt" | "numberOfSigns" | "localTotal" | "localPrice" | "deletable" | "destroyPath" | "confirmPath"
  >
  business: boolean
}

const UnconfirmedOrder: React.FC<UnconfirmedOrderProps> = ({ order, business }) => {
  const { t } = useTranslation()

  return (
    <tr id={`order-${order.id}`}>
      <td className="date">{shortdate(order.createdAt)}</td>
      <td className="signcount">{order.numberOfSigns}</td>
      <td className="prices hidden md:table-cell">{business ? order.localPrice : order.localTotal}</td>
      <td className="hidden md:table-cell"></td>
      <td className="hidden sm:table-cell"></td>
      <td className="controls">
        {order.deletable && (
          <ConfirmLink
            prompt={t("misc.remove")}
            className="delete_link button button-subtle button-error button-medium icon-only mb-0"
            href={order.destroyPath}
          >
            <TrashFill />
          </ConfirmLink>
        )}
        <a href={order.confirmPath} className="button button-primary button-medium view-link mb-0">
          {t("misc.show")}
        </a>
      </td>
    </tr>
  )
}

const UnconfirmedOrderSkeleton: React.FC = () => {
  return (
    <tr>
      <td className="date">
        <span className="skeleton">08/18/2020</span>
      </td>
      <td className="signcount">
        <span className="skeleton">2</span>
      </td>
      <td className="prices hidden md:table-cell">
        <span className="skeleton">€46.46</span>
      </td>
      <td className="hidden md:table-cell"></td>
      <td className="hidden sm:table-cell"></td>
      <td className="controls">
        <a className="delete_link skeleton">
          <TrashFill />
        </a>
        <a className="button button-primary button-medium view-link skeleton mb-0">Show</a>
      </td>
    </tr>
  )
}

interface HistoryProps {
  customer: string
  admin: boolean
  clientRole?: ClientRole
}

const History: React.FC<HistoryProps> = ({ customer, admin, clientRole }) => {
  const { t } = useTranslation()

  const [previousSignsLimit, setPreviousSignsLimit] = useState(5)
  const [confirmedOrdersExpanded, setConfirmedOrdersExpanded] = useState(false)
  const [cancelledOrdersExpanded, setCancelledOrdersExpanded] = useState(false)
  const [unconfirmedOrdersExpanded, setUnconfirmedOrdersExpanded] = useState(false)
  const [openPreview, previewProps] = useSignPreview()
  const business = clientRole == ClientRole.business

  const result = useQuery(historyDoc, { fetchPolicy: "network-only", skip: !customer })
  const loading = !customer || result.loading

  const showMoreSigns = useCallback(() => {
    setPreviousSignsLimit(previousSignsLimit + 5)
  }, [previousSignsLimit])

  const toggleConfirmedOrdersExpanded = useCallback(() => {
    setConfirmedOrdersExpanded(!confirmedOrdersExpanded)
  }, [confirmedOrdersExpanded])

  const toggleCancelledOrdersExpanded = useCallback(() => {
    setCancelledOrdersExpanded(!cancelledOrdersExpanded)
  }, [cancelledOrdersExpanded])

  const toggleUnconfirmedOrdersExpanded = useCallback(() => {
    setUnconfirmedOrdersExpanded(!unconfirmedOrdersExpanded)
  }, [unconfirmedOrdersExpanded])

  const previousSigns = result.data?.currentCustomer?.previousSigns || []
  const confirmedOrders = result.data?.currentCustomer?.confirmedOrders || []
  const cancelledOrders = result.data?.currentCustomer?.cancelledOrders || []
  const unconfirmedOrders = result.data?.currentCustomer?.unconfirmedOrders || []

  return (
    <Shell customer={customer} orders>
      <div className="customerarea orders">
        <h4 className="orders-header signs">
          <span className="ball blue"></span>
          {t("customer.previous_signs")}
        </h4>

        <div className="signslist customerzone">
          <table className="table w-full">
            <thead>
              <tr>
                <th>{t("signslist.sign")}</th>
                <th className="hidden sm:table-cell">{t("article.product_type")}</th>
                <th className="hidden md:table-cell">{t("sign.dimensions")}</th>
                <th className="hidden md:table-cell">{t("article.fix_method")}</th>
                <th className="hidden md:table-cell">{t("article.color")}</th>
                <th className="hidden md:table-cell">
                  {business ? t("article.price_exc_vat") : t("article.price_inc_vat")}
                </th>
                <th>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {previousSigns.map((sign, i) =>
                i >= previousSignsLimit ? null : (
                  <PreviousSign key={i} openPreview={openPreview} sign={sign} admin={admin} business={business} />
                )
              )}
              {loading && Util.repeatElement(3, PreviousSignSkeleton)}
            </tbody>
          </table>
        </div>

        <SignPreview {...previewProps} />

        {previousSigns.length > 5 && (
          <div className="loadsignsbtn center">
            <a
              className={cn("button button-primary button-medium loadsigns", {
                disabled: previousSigns.length <= previousSignsLimit,
              })}
              onClick={showMoreSigns}
            >
              {t("customer.viewall")}
            </a>
          </div>
        )}

        <h4 className="orders-header confirmed">
          <span className="ball green"></span>
          {t("customer.confirmed_orders")}
        </h4>

        <table className="table-striped table-condensed orders confirmed table w-full">
          <thead>
            <tr>
              <th className="date">{t("misc.date")}</th>
              <th className="ordernr">{t("misc.ordernr")}</th>
              <th className="signcount hidden md:table-cell">{t("misc.sign_count")}</th>
              <th className="prices hidden md:table-cell">
                {business ? t("article.total_exc_vat") : t("article.total_inc_vat")}
              </th>
              <th className="hidden md:table-cell">{t("misc.document")}</th>
              <th className="payment hidden sm:table-cell">{t("order.payment")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {confirmedOrders.map((order, i) =>
              !confirmedOrdersExpanded && i >= 10 ? null : <ConfirmedOrder key={i} order={order} business={business} />
            )}
            {result.loading && Util.repeatElement(3, ConfirmedOrderSkeleton)}
          </tbody>
        </table>

        {confirmedOrders.length > 10 && (
          <div className="expandbtn center">
            <a
              className={cn("button button-primary button-medium", { disabled: confirmedOrders.length <= 10 })}
              onClick={toggleConfirmedOrdersExpanded}
            >
              {t(confirmedOrdersExpanded ? "customer.viewless" : "customer.viewall")}
            </a>
          </div>
        )}

        <h4 className="orders-header cancelled">
          <span className="ball red"></span>
          {t("order.cancelled_orders")}
        </h4>

        <table className="table-striped table-condensed orders cancelled table w-full">
          <thead>
            <tr>
              <th className="date">{t("misc.date")}</th>
              <th className="ordernr">{t("misc.ordernr")}</th>
              <th className="signcount hidden md:table-cell">{t("misc.sign_count")}</th>
              <th className="prices hidden md:table-cell">
                {business ? t("article.total_exc_vat") : t("article.total_inc_vat")}
              </th>
              <th className="hidden md:table-cell"></th>
              <th className="hidden sm:table-cell"></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {cancelledOrders.map((order, i) =>
              !cancelledOrdersExpanded && i >= 10 ? null : <CancelledOrder key={i} order={order} business={business} />
            )}
            {result.loading && Util.repeatElement(3, CancelledOrderSkeleton)}
          </tbody>
        </table>

        {cancelledOrders.length > 10 && (
          <div className="expandbtn center">
            <a
              className={cn("button button-primary button-medium", { disabled: cancelledOrders.length <= 10 })}
              onClick={toggleCancelledOrdersExpanded}
            >
              {t(cancelledOrdersExpanded ? "customer.viewless" : "customer.viewall")}
            </a>
          </div>
        )}

        <h4 className="orders-header unconfirmed">
          <span className="ball orange"></span>
          {t("order.unconfirmed_orders")}
        </h4>

        <table className="table-striped table-condensed orders unconfirmed table w-full">
          <thead>
            <tr>
              <th className="date">{t("misc.date")}</th>
              <th className="signcount">{t("misc.sign_count")}</th>
              <th className="prices hidden md:table-cell">
                {business ? t("article.total_exc_vat") : t("article.total_inc_vat")}
              </th>
              <th className="hidden md:table-cell"></th>
              <th className="hidden sm:table-cell"></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {unconfirmedOrders.map((order, i) =>
              !unconfirmedOrdersExpanded && i >= 10 ? null : (
                <UnconfirmedOrder key={i} order={order} business={business} />
              )
            )}
            {result.loading && Util.repeatElement(3, UnconfirmedOrderSkeleton)}
          </tbody>
        </table>

        {unconfirmedOrders.length > 10 && (
          <div className="expandbtn center">
            <a
              className={cn("button button-primary button-medium", { disabled: unconfirmedOrders.length <= 10 })}
              onClick={toggleUnconfirmedOrdersExpanded}
            >
              {t(unconfirmedOrdersExpanded ? "customer.viewless" : "customer.viewall")}
            </a>
          </div>
        )}
      </div>
    </Shell>
  )
}

export default History
